import { OnboardingMessages } from './messages';

/**
 * English (default) translation for the onboarding plugin.
 *
 * When migrating to Backstage's `createTranslationRef`, copy these values
 * into the `messages` record of the ref — the keys are identical.
 */
export const en: OnboardingMessages = {
  /* ── Page chrome ────────────────────────────────── */
  rolePageTitle: 'Developer Onboarding',
  rolePageSubtitle: 'Learn to navigate the IDP',
  trackPageSubtitle: 'Learn to navigate the IDP',

  /* ── Role selection page ────────────────────────── */
  roleQuestion: 'What is your role?',
  startAs: 'Start as {{role}}',
  comingSoon: 'Coming soon',

  /* ── Track page ─────────────────────────────────── */
  changeRole: 'Change Role',
  startJourney: 'Start Journey',
  reviewJourney: 'Review Journey',
  warningMessage:
    'You should complete the onboarding process to use the platform!',
  journeyLabel: 'Journey {{index}}',
  stepsCount: '{{count}} steps',
  phasesCount: '{{count}} phases',
  journeysCount: '{{count}} journeys',
  completed: 'Completed',

  /* ── Step modal / overlay ───────────────────────── */
  back: 'Back',
  continueLabel: 'Continue',
  finish: 'Finish',
  stepIndicator: 'Step {{current}}/{{total}}',

  /* ── Roles ──────────────────────────────────────── */
  roleDeveloperTitle: 'Developer',
  roleDeveloperDescription: 'I code and deploy my service',
  roleDeveloperDuration: '~70 min',
  roleDeveloperBadge: '+ Quiz',
  roleDeveloperPoint1: 'Navigate the Catalog & find services',
  roleDeveloperPoint2: 'Deploy from code to production',
  roleDeveloperPoint3: 'Troubleshoot pipeline & runtime issues',

  roleTechLeadTitle: 'Tech Lead',
  roleTechLeadDescription:
    "I create services and manage my team's infrastructure",
  roleTechLeadDuration: '~75 min',
  roleTechLeadBadge: '+ Exam',
  roleTechLeadPoint1: 'Everything in Developer track',
  roleTechLeadPoint2: 'Create new services from templates',
  roleTechLeadPoint3:
    'Provision Azure resources (KeyVault, Storage, EventHub)',
  roleTechLeadPoint4: 'API governance & scorecards',

  /* ── Journey titles ─────────────────────────────── */
  journeyNavigatingTitle: 'Navigating the IDP',
  journeyDeployTitle: 'Deploy: From Code to Live',
  journeyTroubleshootTitle: 'When Something Goes Wrong',
  journeyTemplatesTitle: 'Create new services from templates',
  journeyAzureTitle: 'Provision Azure resources',
  journeyApiTitle: 'API governance & scorecards',
  journeyTeamTitle: 'Team Management & Permissions',
  journeyCostTitle: 'Cost Optimization & Monitoring',

  /* ── Journey descriptions ───────────────────────── */
  journeyNavigatingDesc:
    'Understand the portal, find your project, and learn the tools and rules.',
  journeyDeployDesc:
    'Learn how to deploy your services from code to production.',
  journeyTroubleshootDesc:
    'Troubleshoot pipeline issues and runtime problems effectively.',
  journeyTemplatesDesc:
    'Spin up new services and components using predefined templates.',
  journeyAzureDesc:
    'Set up and manage Azure cloud resources for your team.',
  journeyApiDesc:
    'Ensure API quality and compliance with governance scorecards.',
  journeyTeamDesc:
    'Manage your team members and configure access permissions.',
  journeyCostDesc:
    'Monitor costs and optimize resource usage across your services.',

  /* ── Journey durations ──────────────────────────── */
  journeyNavigatingDurationDev: '~15 min',
  journeyNavigatingDurationTL: '~10 min',
  journeyDeployDuration: '~45 min',
  journeyTroubleshootDuration: '~10 min',
  journeyTemplatesDuration: '~10 min',
  journeyAzureDuration: '~15 min',
  journeyApiDuration: '~15 min',
  journeyTeamDuration: '~10 min',
  journeyCostDuration: '~10 min',

  /* ── Phase names — Navigating the IDP ───────────── */
  phaseWelcome: 'Welcome & Platform Overview',
  phaseNavigating: 'Navigating Backstage',
  phaseFinding: 'Finding Your Project',
  phaseExternal: 'External Tools Overview',
  phasePermissions: 'Permissions & Security Rules',

  /* ── Phase names — Deploy ───────────────────────── */
  phasePipeline: 'Pipeline Overview',
  phaseBuild: 'Build & Test',
  phaseStaging: 'Deploy to Staging',
  phaseProduction: 'Promote to Production',
  phaseMonitoringRollback: 'Monitoring & Rollback',

  /* ── Phase names — Troubleshoot ─────────────────── */
  phaseIdentifying: 'Identifying Issues',
  phaseReadingLogs: 'Reading Logs',
  phasePipelineErrors: 'Common Pipeline Errors',
  phaseDebugging: 'Runtime Debugging',
  phaseEscalation: 'Escalation Procedures',

  /* ── Phase names — Templates ────────────────────── */
  phaseTemplateGallery: 'Template Gallery',
  phaseCreatingComponent: 'Creating a Component',
  phaseConfiguring: 'Configuring Parameters',
  phaseRepoSetup: 'Repository Setup',
  phaseFirstDeploy: 'First Deployment',

  /* ── Phase names — Azure ────────────────────────── */
  phaseAzureOverview: 'Azure Resources Overview',
  phaseKeyVault: 'KeyVault Setup',
  phaseStorage: 'Storage Account',
  phaseEventHub: 'EventHub Configuration',
  phaseResourceMonitoring: 'Resource Monitoring',

  /* ── Phase names — API ──────────────────────────── */
  phaseApiStandards: 'API Standards',
  phaseScorecard: 'Scorecard Overview',
  phaseRegisterApis: 'Registering APIs',
  phaseCompliance: 'Compliance Checks',
  phaseImprovingScores: 'Improving Scores',

  /* ── Phase names — Team ─────────────────────────── */
  phaseTeamStructure: 'Team Structure',
  phaseRoleAccess: 'Role-Based Access',
  phasePermissionPolicies: 'Permission Policies',
  phaseOnboardingMembers: 'Onboarding Members',

  /* ── Phase names — Cost ─────────────────────────── */
  phaseCostDashboard: 'Cost Dashboard',
  phaseUsageAnalysis: 'Usage Analysis',
  phaseOptimization: 'Optimization Tips',
  phaseBudgetAlerts: 'Budget Alerts',

  /* ── Journey step section titles ────────────────── */
  sectionWelcomePlatform: '1. Welcome & Platform Overview',
  sectionNavigatingBackstage: '2. Navigating Backstage',
  sectionFindingProject: '3. Finding Your Project',
  sectionExternalTools: '4. External Tools Overview',
  sectionPermissionsSecurity: '5. Permissions & Security Rules',
  sectionBeforeStart: '1. Before you start',
  sectionCICDPipeline: '2. The CI/CD pipeline',
  sectionConfiguringApp: '3. Configuring your application',
  sectionBranchesLive: '4. Branches & going live',
  sectionMonitoringDeploys: '5. Monitoring your deploys',

  /* ── Journey step titles ────────────────────────── */
  stepWelcomeIdp: 'Welcome to the IDP',
  stepToolsAroundYou: 'The tools around you',
  stepHowItConnects: 'How it all connects',
  stepHomePage: 'The home page',
  stepQuickActions: 'Quick actions',
  stepSearchBar: 'The search bar',
  stepOpenCatalog: 'Open the catalog',
  stepUnderstandingKinds: 'Understanding kinds',
  stepFiltersSearch: 'Filters & search',
  stepFindSystem: 'Find your system',
  stepSystemOverview: 'System overview',
  stepRelationsGraph: 'Relations graph',
  stepArgoCd: 'ArgoCD: your deploy dashboard',
  stepGrafana: 'Grafana: your logs',
  stepGitHubAzureDevOps: 'GitHub & Azure DevOps',
  stepPermissionsDeveloper: 'Your permissions as developer',
  stepSecurityRules: 'Security rules (must know)',
  stepJourneyComplete: 'Journey complete!',
  stepTwoRepositories: 'Two repositories, one flow',
  stepPlatformHandles: 'What the platform handles for you',
  stepCompleteFlow: 'The complete flow: push to running app',
  stepPipelineStages: 'Three pipeline stages',
  stepArgoCDAutoDeploy: 'ArgoCD deploys automatically',
  stepComponentFile: 'The component file',
  stepEnvSecrets: 'Environment variables & secrets',
  stepResourcesHealth: 'Resources & health checks',
  stepValidateYAML: 'Validate your YAML before committing',
  stepBranchMapping: 'Which branch updates which environment',
  stepGoingProduction: 'Going to production',
  stepIndependentlyDo: 'What you can do on your own',
  stepCheckingArgoCD: 'Checking deploys in ArgoCD',
  stepCheckingLogs: 'Checking logs in Grafana',

  /* -- Journey 3: When Something Goes Wrong ----------- */
  sectionJ3CheckLogs: '1. Check your logs first',
  stepJ3StartGrafana: 'Start with Grafana',
  stepJ3WhatToLookLogs: 'What to look for in logs',
  sectionJ3CheckDeploy: '2. Check your deploy',
  stepJ3WhenNotInLogs: 'When it is not in the logs',
  stepJ3GitOpsMisconfiguration: 'GitOps misconfiguration',
  stepJ3PipelineFailures: 'Pipeline failures',
  stepJ3ImageNotUpdating: 'Image not updating (PullBackOff)',
  sectionJ3AskHelp: '3. When to ask for help',
  stepJ3OpenTicket: 'Open a ticket',
  stepJ3Journey3Complete: 'Journey complete!',

  /* ── Quiz ────────────────────────────────────────── */
  sectionQuiz: 'Quiz',
  stepQuiz: 'Quiz',
  quizQuestionOf: 'Question {{current}}/{{total}}',
  quizConfirm: 'Confirm',
  quizCorrectAnswer: 'Correct answer: {{letter}}',
  quizPassed: 'Quiz passed!',
  quizFailed: 'Quiz not passed',
  quizCorrectAnswers: 'correct answers',
  quizNextJourneyUnlocked: 'Journey 2: Deploy: from code to live is now unlocked!',
  quizNextSteps: 'You have completed all three Developer journeys and passed all validations. Full platform access is now unlocked.',
  quizContinueToJourney: 'Continue to Journey 2',
  quizFinishOnboarding: 'Finish Onboarding',
  quizTryAgain: 'Try again',
  quizStartQuiz: 'Start Quiz',
  quizFailedMessage:
    'You need at least {{min}} out of {{total}} correct to pass. Review the journey content and try again. You can retake the quiz as many times as you need.',
  quizReviewJourney: 'Review Journey',
  playgroundGoToPlayground: 'Go to Playground',

  /* ── Playground ───────────────────────────────────── */
  sectionPlayground: 'Playground',
  stepPlayground: 'Playground',
  playgroundTitle: 'Playground Time!',
  playgroundDescription: 'Now it is your turn. Complete the tasks below in the Playground repository to validate what you learned.',
  playgroundTaskListHeader: 'What you will do:',
  playgroundTask1: 'Clone the repo, create a branch, make a change',
  playgroundTask2: 'Merge to develop and watch the pipeline run',
  playgroundTask3: 'Check the deploy in ArgoCD (dv environment)',
  playgroundTask4: 'Promote to pre-production via release_candidate branch',
  playgroundTask5: 'Promote to production via merge to main + PR approval',
  playgroundTutorialLabel: 'Open the full tutorial',
  playgroundTutorialDescription: 'Step-by-step instructions for the exercise',
  playgroundRepoLabel: 'Open playground repository (BFF)',
  playgroundVerifyHint: 'When you are done, click Verify below. We will check if the tasks were completed.',
  playgroundVerify: 'Verify',
  playgroundCompleted: 'Playground completed!',
  playgroundCompletedMessage: 'We verified your actions in the repository. Everything checks out.',
  playgroundNextJourneyUnlocked: 'Journey 3: When Something Goes Wrong is now unlocked! You can start it from the onboarding page.',
  playgroundContinueToJourney: 'Continue to Journey 3',
  playgroundFailedTitle: "We couldn't confirm completion",
  playgroundFailedMessage: 'We checked the Playground repository but could not verify that all tasks were completed. You may still have some actions left to do.',
  playgroundFailedHint: 'Review the journey content, make sure you completed all the steps in the repository, and try verifying again.',
  playgroundReviewJourney: 'Review Journey',
  playgroundVerifyAgain: 'Verify again',

  /* ── Exit confirmation ───────────────────────────── */
  exitConfirmTitle: 'Leave onboarding?',
  exitConfirmMessage: 'Your progress in this journey will be lost. You can restart it anytime from the track page.',
  exitConfirmLeave: 'Leave',
  exitConfirmStay: 'Stay',

  /* ── Language selector ──────────────────────────── */
  languageLabel: 'Language',

  /* ── Prerequisites ──────────────────────────────── */
  prerequisitesTitle: 'Prerequisites',
  prerequisitesDescription: 'Before starting the onboarding, make sure you have the following licenses. If you don\'t have any of them, request through the links below.',
  prerequisiteGithubLicense: 'GitHub License',
  prerequisiteGithubCopilot: 'GitHub Copilot License',
  prerequisiteGithubCodespaces: 'GitHub Codespaces License',
  prerequisitesAlignWithTechLead: 'If you don\'t have any of these licenses, align with your Tech Lead as well.',
  prerequisitesRequestAccess: 'Request access here',
  prerequisitesHowToFill: 'How to fill the request',

  /* ── NPS ─────────────────────────────────────────── */
  npsTitle: 'Onboarding Feedback',
  npsDescription: 'Your opinion is key to improving the IDP onboarding experience. This survey is anonymous and takes about 2 minutes. The results will guide the next platform evolutions.',
  npsQuestionsCount: '{{count}} questions',
  npsBadge: '+ NPS',
  npsStart: 'Start NPS',
  npsSubmitError: 'Failed to submit NPS. Please try again.',
  reviewNps: 'Review NPS',
  npsThankYouSubtitle: 'Your answers have been recorded successfully.',
};
